import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { fetchAllHouses } from '../redux/action/house';
import { messages } from '../common/notification';
import { loadHouseForPicker, moveDevice } from './api';
import { DEVICE_GROUPS, theme } from './constants';
import {
  buildHouseOptions,
  collectHouseDevices,
  deviceKey,
  houseSummary,
  matchesSearch,
} from './function';
import ConfirmMoveModal from './components/ConfirmMoveModal';
import DeviceList from './components/DeviceList';
import MoveActionBar from './components/MoveActionBar';
import MoveResultBanner from './components/MoveResultBanner';
import PropertyPanel from './components/PropertyPanel';
import PropertySelect from './components/PropertySelect';
import StepIndicator from './components/StepIndicator';
import { ArrowRightIcon, LockIcon } from './components/Icons';
import { canAccessDeviceMove } from './access';
import './DeviceMoved.css';

const emptyCounts = { total: 0, groups: [] };

function DeviceMoved() {
  const housesList = useSelector((state) => state.housesList);
  const mutipleHouse = useSelector((state) => state.mutipleHouse);
  const login = useSelector((state) => state.login);
  const houseData = useSelector((state) => state.houseData);

  const [sourceHouseId, setSourceHouseId] = useState(null);
  const [targetHouseId, setTargetHouseId] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [loadingSource, setLoadingSource] = useState(false);
  const [loadingTarget, setLoadingTarget] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [moving, setMoving] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [lastMove, setLastMove] = useState(null);

  const mobile = login?.profile?.mobile?.slice(3);
  const houseOptions = useMemo(() => buildHouseOptions(housesList), [housesList]);

  // Spinner is picked from which column the house is showing in, so houses that
  // are not on screen (an undone move's other side) can be refreshed too.
  const loadHouse = async (houseId, { force = false } = {}) => {
    if (!houseId || !mobile) return;
    if (!force && mutipleHouse[houseId]) return;
    const setLoading =
      houseId === sourceHouseId ? setLoadingSource : houseId === targetHouseId ? setLoadingTarget : null;
    if (setLoading) setLoading(true);
    const res = await loadHouseForPicker(houseId, mobile);
    if (setLoading) setLoading(false);
    if (res && res.success === false) {
      messages.error(res.message || 'Could not load this property');
    }
  };

  useEffect(() => {
    if (!houseOptions.length && mobile) fetchAllHouses(mobile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobile]);

  useEffect(() => {
    loadHouse(sourceHouseId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceHouseId]);

  useEffect(() => {
    loadHouse(targetHouseId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetHouseId]);

  const sourceHouse = sourceHouseId ? mutipleHouse[sourceHouseId] : null;
  const targetHouse = targetHouseId ? mutipleHouse[targetHouseId] : null;

  const allDevices = useMemo(() => collectHouseDevices(sourceHouse), [sourceHouse]);

  const counts = useMemo(() => {
    if (!allDevices.length) return emptyCounts;
    const groups = DEVICE_GROUPS.map((group) => ({
      key: group.key,
      label: group.label,
      color: group.color,
      count: allDevices.filter((device) => device.groupKey === group.key).length,
    })).filter((group) => group.count > 0);
    return { total: allDevices.length, groups };
  }, [allDevices]);

  const visibleDevices = useMemo(
    () =>
      allDevices.filter(
        (device) => (typeFilter === 'all' || device.groupKey === typeFilter) && matchesSearch(device, search)
      ),
    [allDevices, typeFilter, search]
  );

  const selectedDevices = useMemo(
    () => selectedKeys.map((key) => allDevices.find((device) => deviceKey(device) === key)).filter(Boolean),
    [allDevices, selectedKeys]
  );

  const sourceSummary = useMemo(() => houseSummary(sourceHouse), [sourceHouse]);
  const targetSummary = useMemo(() => houseSummary(targetHouse), [targetHouse]);

  const sourceHouseName = houseOptions.find((option) => option.value === sourceHouseId)?.label || null;
  const targetHouseName = houseOptions.find((option) => option.value === targetHouseId)?.label || null;

  const handleSourceChange = (houseId) => {
    setSourceHouseId(houseId);
    setSelectedKeys([]);
    setSearch('');
    setTypeFilter('all');
    // The same property can never sit on both sides of the move.
    if (houseId && houseId === targetHouseId) {
      setTargetHouseId(null);
    }
  };

  const handleTargetChange = (houseId) => {
    if (houseId && houseId === sourceHouseId) {
      messages.error('Pick a property different from the current one');
      return;
    }
    setTargetHouseId(houseId);
  };

  const handleDeviceSelect = (device) => {
    const key = deviceKey(device);
    setSelectedKeys((prev) => (prev.includes(key) ? prev.filter((existing) => existing !== key) : [...prev, key]));
  };

  // Toggles every currently visible (search/type-filtered) device at once.
  const handleSelectAllVisible = () => {
    const visibleKeys = visibleDevices.map(deviceKey);
    setSelectedKeys((prev) => {
      const allSelected = visibleKeys.length > 0 && visibleKeys.every((key) => prev.includes(key));
      if (allSelected) return prev.filter((key) => !visibleKeys.includes(key));
      return Array.from(new Set([...prev, ...visibleKeys]));
    });
  };

  const handleSwap = () => {
    if (!sourceHouseId && !targetHouseId) return;
    const nextSource = targetHouseId;
    const nextTarget = sourceHouseId;
    setSourceHouseId(nextSource);
    setTargetHouseId(nextTarget);
    setSelectedKeys([]);
    setSearch('');
    setTypeFilter('all');
  };

  const startNewMove = () => {
    setSourceHouseId(null);
    setTargetHouseId(null);
    setSelectedKeys([]);
    setSearch('');
    setTypeFilter('all');
    setLastMove(null);
  };

  const canMove = !!(sourceHouseId && selectedDevices.length && targetHouseId && targetHouseId !== sourceHouseId);

  const blockedReason = !sourceHouseId
    ? 'Select the property the device is on'
    : !selectedDevices.length
    ? 'Select at least one device to move'
    : !targetHouseId
    ? 'Select the new property'
    : '';

  const currentStep = !sourceHouseId ? 1 : !selectedDevices.length ? 2 : 3;

  // Route only accepts one device per call, so multiple selections are moved
  // one request at a time; devices that fail stay selected so the move can be retried.
  const handleConfirmMove = async () => {
    if (!canMove) return;
    setMoving(true);

    const outcomes = [];
    for (const device of selectedDevices) {
      const result = await moveDevice({
        deviceId: device.deviceId,
        oldHouseId: sourceHouseId,
        newHouseId: targetHouseId,
      });
      outcomes.push({ device, result });
    }

    setMoving(false);

    const moved = outcomes.filter((outcome) => outcome.result.success);
    const failed = outcomes.filter((outcome) => !outcome.result.success);

    if (moved.length) {
      messages.success(
        moved.length === 1
          ? `${moved[0].device.name} moved to ${targetHouseName}`
          : `${moved.length} devices moved to ${targetHouseName}`
      );
      setLastMove({
        devices: moved.map(({ device }) => ({ deviceId: device.deviceId, deviceName: device.name })),
        fromHouseId: sourceHouseId,
        toHouseId: targetHouseId,
        from: sourceHouseName,
        to: targetHouseName,
        undone: false,
      });
    }

    if (failed.length) {
      messages.error(
        failed.map(({ device, result }) => `${device.name}: ${result.message || 'could not be moved'}`).join(' · ')
      );
    }

    setConfirmOpen(false);
    setSelectedKeys(failed.map(({ device }) => deviceKey(device)));
    setSearch('');
    setTypeFilter('all');

    if (moved.length) {
      // Both properties changed on the server — pull them again so the lists match.
      await loadHouse(sourceHouseId, { force: true });
      await loadHouse(targetHouseId, { force: true });
    }
  };

  /**
   * Reverses the last move by calling the same route with the two houses
   * swapped for every device that was moved — the ids come off `lastMove`, so
   * whatever is selected in the form now does not matter.
   */
  const handleUndo = async () => {
    if (!lastMove || lastMove.undone || undoing) return;

    setUndoing(true);
    const outcomes = [];
    for (const device of lastMove.devices) {
      const result = await moveDevice({
        deviceId: device.deviceId,
        oldHouseId: lastMove.toHouseId,
        newHouseId: lastMove.fromHouseId,
      });
      outcomes.push({ device, result });
    }
    setUndoing(false);

    const undone = outcomes.filter((outcome) => outcome.result.success);
    const failed = outcomes.filter((outcome) => !outcome.result.success);

    if (undone.length) {
      messages.success(
        undone.length === 1
          ? `${undone[0].device.deviceName} moved back to ${lastMove.from}`
          : `${undone.length} devices moved back to ${lastMove.from}`
      );
    }

    if (failed.length) {
      messages.error(
        failed
          .map(({ device, result }) => `${device.deviceName}: ${result.message || 'could not be undone'}`)
          .join(' · ')
      );
    }

    // Only the devices that failed are still sitting on `toHouseId` — keep
    // just those on `lastMove` so a retry does not re-undo ones already back.
    setLastMove(
      failed.length ? { ...lastMove, devices: failed.map(({ device }) => device) } : { ...lastMove, undone: true }
    );
    setSelectedKeys([]);

    if (undone.length) {
      await loadHouse(lastMove.toHouseId, { force: true });
      await loadHouse(lastMove.fromHouseId, { force: true });
    }
  };

  // Offered only while the dashboard's selected property belongs to an
  // allowlisted client — a direct URL hit lands on the notice below.
  if (!canAccessDeviceMove(houseData)) {
    return (
      <div
        className="deviceMovedPage dmFadeIn"
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
        }}
      >
        <div
          style={{
            maxWidth: 420,
            textAlign: 'center',
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: 12,
            padding: '32px 30px',
          }}
        >
          <span
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              margin: '0 auto 14px',
              background: theme.surfaceMuted,
              color: theme.textMuted,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LockIcon size={20} />
          </span>
          <div style={{ fontSize: 15.5, fontWeight: 700, color: theme.text }}>Not available for this property</div>
          <div style={{ fontSize: 13, color: theme.textMuted, marginTop: 7, lineHeight: '19px' }}>
            Moving devices between properties is enabled for selected clients only.
            {houseData?.houseName ? (
              <>
                {' '}
                Switch to a property that has it, or continue on{' '}
                <strong style={{ color: theme.text, fontWeight: 700 }}>{houseData.houseName}</strong>.
              </>
            ) : (
              ' Select a property from the dashboard first.'
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="deviceMovedPage" style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 24,
          flexWrap: 'wrap',
          marginBottom: 18,
        }}
      >
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#3E3E3E', lineHeight: '28px' }}>Device Moved</div>
          <div style={{ fontSize: 13, color: theme.textMuted, marginTop: 3 }}>
            Move a device from one property to another.
          </div>
        </div>
        <StepIndicator currentStep={currentStep} />
      </div>

      {lastMove && (
        <MoveResultBanner
          move={lastMove}
          undoing={undoing}
          onUndo={handleUndo}
          onClear={startNewMove}
          onDismiss={() => setLastMove(null)}
        />
      )}

      <div className="dmPropertyGrid">
        <PropertyPanel
          step={1}
          title="Current Property"
          hint="Where the device is today"
          summary={sourceSummary}
          loading={loadingSource}
          onRefresh={sourceHouseId ? () => loadHouse(sourceHouseId, { force: true }) : null}
        >
          <PropertySelect
            options={houseOptions}
            value={sourceHouseId}
            onChange={handleSourceChange}
            placeholder="Select current property"
            lockedValue={targetHouseId}
            lockedHint="New property"
          />
        </PropertyPanel>

        <button
          className="dmSwap"
          onClick={handleSwap}
          disabled={!sourceHouseId && !targetHouseId}
          title="Swap properties"
          style={{
            width: 34,
            height: 34,
            borderRadius: '50%',
            border: `1px solid ${theme.border}`,
            background: theme.surface,
            color: sourceHouseId || targetHouseId ? theme.accent : theme.textFaint,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: sourceHouseId || targetHouseId ? 'pointer' : 'not-allowed',
            boxShadow: '0 1px 4px rgba(17,24,39,0.08)',
            flexShrink: 0,
          }}
        >
          <ArrowRightIcon size={16} />
        </button>

        <PropertyPanel
          step={3}
          title="New Property"
          hint="Where the device should land"
          tone="#10b981"
          toneSoft="rgba(16,185,129,0.1)"
          summary={targetSummary}
          loading={loadingTarget}
          onRefresh={targetHouseId ? () => loadHouse(targetHouseId, { force: true }) : null}
          locked={!selectedDevices.length}
          lockedNote={sourceHouseId ? 'Pick a device first' : 'Pick the current property first'}
        >
          <PropertySelect
            options={houseOptions}
            value={targetHouseId}
            onChange={handleTargetChange}
            placeholder={selectedDevices.length ? 'Select new property' : 'Waiting for a device…'}
            lockedValue={sourceHouseId}
            lockedHint="Current property"
            isDisabled={!selectedDevices.length}
          />
        </PropertyPanel>
      </div>

      <div style={{ marginTop: 16 }}>
        <DeviceList
          devices={visibleDevices}
          counts={counts}
          loading={loadingSource}
          hasHouse={!!sourceHouseId}
          search={search}
          onSearchChange={setSearch}
          typeFilter={typeFilter}
          onTypeChange={setTypeFilter}
          selectedKeys={selectedKeys}
          onSelect={handleDeviceSelect}
          onToggleSelectAll={handleSelectAllVisible}
        />
      </div>

      <MoveActionBar
        devices={selectedDevices}
        fromHouseName={sourceHouseName}
        toHouseName={targetHouseName}
        canMove={canMove}
        blockedReason={blockedReason}
        onMove={() => setConfirmOpen(true)}
      />

      <ConfirmMoveModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        devices={selectedDevices}
        fromHouse={sourceHouseName}
        toHouse={targetHouseName}
        onConfirm={handleConfirmMove}
        loading={moving}
      />
    </div>
  );
}

export default DeviceMoved;
